import React, { useState, useMemo, useCallback } from "react";
import {
  Box, IconButton, Divider, ListItemIcon,
  ListItemText, Menu, MenuItem, Avatar, Typography,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { useRouter } from "next/router";

import { dataConstants } from "../../constants";
import useAuth from "../../hook/auth";

const NavTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: { color: alpha('#0a0e1a', 0.9) },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: alpha('#0a0e1a', 0.9),
    fontWeight: 500,
    color: '#fff',
    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
    fontSize: 11,
    padding: '5px 10px',
    borderRadius: '6px',
    letterSpacing: '0.01em',
  },
}));

const MENU_PAPER_PROPS = {
  elevation: 0,
  sx: {
    mt: 1.5,
    borderRadius: '14px',
    minWidth: '230px',
    boxShadow: '0 4px 6px -2px rgba(0,0,0,0.06), 0 12px 36px -4px rgba(0,0,0,0.12)',
    border: '1px solid rgba(0,0,0,0.06)',
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      right: 18,
      width: 10,
      height: 10,
      bgcolor: 'background.paper',
      transform: 'translateY(-50%) rotate(45deg)',
      zIndex: 0,
      borderTop: '1px solid rgba(0,0,0,0.06)',
      borderLeft: '1px solid rgba(0,0,0,0.06)',
    },
  },
};

export default function UserMenu({ pageLoading }) {
  const router = useRouter();
  const auth = useAuth() || {};
  const { user } = auth;
  const [anchorElUser, setAnchorElUser] = useState(null);

  const isPremium = useMemo(() => {
    try {
      if (user?.reloadUserInfo?.customAttributes) {
        const attrs = JSON.parse(user.reloadUserInfo.customAttributes);
        return attrs?.userType === "PREMIUM" || attrs?.userType === "PROFESSIONAL";
      }
    } catch (error) {
      console.error("Failed to parse user attributes", error);
    }
    return false;
  }, [user?.reloadUserInfo?.customAttributes]);

  const handleCloseUserMenu = useCallback(() => {
    setAnchorElUser(null);
  }, []);

  const handleNavigate = useCallback((path) => {
    setAnchorElUser(null);
    if (path) router.push(path);
  }, [router]);

  const handleLogout = useCallback(() => {
    if (auth.logout) auth.logout();
    setAnchorElUser(null);
  }, [auth]);

  const handleAvatarClick = useCallback((event) => {
    event.preventDefault();
    if (user?.email) {
      setAnchorElUser(event.currentTarget);
    } else {
      router.push({ pathname: '/auth', query: { from: router?.asPath } });
    }
  }, [user?.email, router]);

  const menuItems = useMemo(
    () => (dataConstants.USERMENU || []).slice(isPremium ? 1 : 0),
    [isPremium]
  );

  const avatarInitial = user?.displayName?.charAt(0)?.toUpperCase()
    ?? user?.email?.charAt(0)?.toUpperCase()
    ?? null;

  const tooltipLabel = user?.email
    ? (dataConstants.MYACCOUNT || "My Account")
    : "Sign in";

  return (
    <Box sx={{ flexGrow: 0, display: "flex" }}>
      <NavTooltip
        title={tooltipLabel}
        arrow
        placement="bottom-end"
        enterDelay={400}
        describeChild
      >
        <IconButton
          aria-label={user?.email ? "Open account menu" : "Sign in to your account"}
          aria-controls={Boolean(anchorElUser) ? "user-menu-appbar" : undefined}
          aria-haspopup="true"
          aria-expanded={Boolean(anchorElUser) ? 'true' : undefined}
          onClick={handleAvatarClick}
          sx={{
            p: { xs: 1, md: 0.5 },
            ml: 1.5,
            borderRadius: '50%',
            '&:focus-visible': {
              outline: '2px solid rgba(255,255,255,0.55)',
              outlineOffset: '2px',
            },
          }}
        >
          {pageLoading ? (
            <Box
              aria-hidden="true"
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.1)',
                '@keyframes navAvatarPulse': {
                  '0%, 100%': { opacity: 0.6 },
                  '50%': { opacity: 0.25 },
                },
                animation: 'navAvatarPulse 1.6s ease-in-out infinite',
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                  opacity: 0.4,
                },
              }}
            />
          ) : (
            <Avatar
              alt={user?.displayName || (user?.email ? 'Account' : 'Sign in')}
              src={user?.photoURL || ""}
              sx={{
                width: 36,
                height: 36,
                fontSize: '0.875rem',
                fontWeight: 600,
                bgcolor: avatarInitial
                  ? 'rgba(255,255,255,0.18)'
                  : 'rgba(255,255,255,0.1)',
                color: '#fff',
                border: '1.5px solid rgba(255,255,255,0.18)',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.45)',
                  boxShadow: '0 0 0 3px rgba(255,255,255,0.1)',
                },
              }}
            >
              {avatarInitial}
            </Avatar>
          )}
        </IconButton>
      </NavTooltip>

      <Menu
        id="user-menu-appbar"
        anchorEl={anchorElUser}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        PaperProps={MENU_PAPER_PROPS}
        TransitionProps={{ timeout: 150 }}
      >
        {user?.email && (
          <>
            <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.875 }}>
                <Avatar
                  src={user?.photoURL || ""}
                  alt={user?.displayName || ''}
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    bgcolor: alpha('#001742', 0.08),
                    color: '#001742',
                    flexShrink: 0,
                  }}
                >
                  {avatarInitial}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  {user?.displayName && (
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: '#0f172a', lineHeight: 1.25, mb: 0.25 }}
                      noWrap
                    >
                      {user.displayName}
                    </Typography>
                  )}
                  {isPremium && (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 0.875,
                        py: 0.15,
                        bgcolor: alpha('#001742', 0.07),
                        borderRadius: '5px',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        color: '#001742',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        lineHeight: 1.6,
                      }}
                    >
                      Premium
                    </Box>
                  )}
                </Box>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: '#64748b',
                  wordBreak: 'break-all',
                  lineHeight: 1.4,
                  fontSize: '0.775rem',
                }}
              >
                {user.email}
              </Typography>
            </Box>
            <Divider sx={{ my: 0 }} />
          </>
        )}

        {menuItems.map((setting, index) => {
          if (setting.divider) {
            return <Divider key={`divider-${index}`} sx={{ my: 0.75 }} />;
          }
          return (
            <MenuItem
              key={`usermenu-${setting.name}`}
              onClick={() => setting.path ? handleNavigate(setting.path) : handleLogout()}
              sx={{
                py: 1,
                px: 2,
                minHeight: 40,
                transition: 'background-color 0.1s ease',
                '&:hover': { bgcolor: '#f8fafc' },
                '&:focus-visible': { bgcolor: '#f1f5f9' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 30, color: setting.color || '#64748b' }}>
                {setting.icon}
              </ListItemIcon>
              <ListItemText
                primary={setting.name}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: setting.color || '#1e293b',
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}